import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { X, FileEarmarkPdf } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import moment from 'moment';
import 'moment/locale/es';
import { getSessionStatisticsBySessionId, getRealTimeSalesStatisticsByLocalId } from '../../../../../../../../crud/management_api/sales_statistics';
import { getSessionPaymentTransactionSummaryByLocalId } from '../../../../../../../../crud/payments_api/payment_transactions';
import { getAllModificationsBySessionId } from '../../../../../../../../crud/pedidos_api/pedidos_modifications';
import { exportToPDF } from './utils';
import {
  SessionHeader,
  SalesMetrics,
  PaymentSummary,
  UserStatsSection,
  TypeStatsSection,
  ShiftsBreakdown,
  Divider,
  SessionFooter,
  ModificationsSummary,
  ExportConfigModal
} from './components';
import type { ReportSection } from './components/SectionSelector/SectionSelector';
import { SessionStatistics, PaymentTransactionSummary } from './types';
import './SessionBriefModal.scss';
import OpenerSalesSection, { attributionCopy } from './components/OpenerSalesSection';

// ============================================
// Component Props
// ============================================

interface SessionBriefModalProps {
  show: boolean;
  onHide: () => void;
  sessionId?: number; // Optional: if not provided, fetches current session
  sessionPeriod?: {
    start: string;
    end: string;
  };
}

// ============================================
// Main Component
// ============================================

const SessionBriefModal: React.FC<SessionBriefModalProps> = ({
  show,
  onHide,
  sessionId,
  sessionPeriod
}) => {
  const [loading, setLoading] = useState(false);
  const [sessionStats, setSessionStats] = useState<SessionStatistics | null>(null);
  const [paymentSummary, setPaymentSummary] = useState<PaymentTransactionSummary | null>(null);
  const [modifications, setModifications] = useState<any[]>([]);
  const [exporting, setExporting] = useState(false);
  const [showExportConfig, setShowExportConfig] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // ============================================
  // Section Configuration
  // ============================================

  const getAvailableSections = (): ReportSection[] => {
    const sections: ReportSection[] = [
      {
        id: 'header',
        label: 'Encabezado',
        description: 'Información del local y periodo de la sesión',
        required: true
      },
      {
        id: 'sales-metrics',
        label: 'Métricas de Ventas',
        description: 'Ventas netas, propinas, descuentos y ticket promedio',
        required: true
      },
      {
        id: 'payment-summary',
        label: 'Formas de Pago',
        description: 'Resumen de transacciones por método de pago'
      }
    ];

    if (sessionStats?.opener_sales) {
      sections.push({ id: 'opener-sales', label: attributionCopy.title,
        description: attributionCopy.explanation });
    }
    // Conditionally add optional sections based on data availability
    if (sessionStats?.users_stats && Object.keys(sessionStats.users_stats).length > 0) {
      sections.push({
        id: 'user-stats',
        label: 'Estadísticas por Usuario',
        description: 'Ventas, propinas y cancelaciones por mesero'
      });
    }

    if (sessionStats?.type_stats && Object.keys(sessionStats.type_stats).length > 0) {
      sections.push({
        id: 'type-stats',
        label: 'Estadísticas por Tipo',
        description: 'Desglose de ventas por tipo de pedido'
      });
    }

    if (sessionStats?.shifts && Object.keys(sessionStats.shifts).length > 0) {
      sections.push({
        id: 'shifts',
        label: 'Desglose por Turno',
        description: 'Métricas detalladas de cada turno'
      });
    }

    if (modifications.length > 0) {
      sections.push({
        id: 'modifications',
        label: 'Modificaciones',
        description: 'Resumen de modificaciones realizadas'
      });
    }

    sections.push({
      id: 'footer',
      label: 'Pie de Página',
      description: 'Fecha y hora de generación del reporte',
      required: true
    });

    return sections;
  };

  // ============================================
  // Section Selection Handlers
  // ============================================

  const handleOpenExportConfig = () => {
    setShowExportConfig(true);
  };

  const handleCloseExportConfig = () => {
    setShowExportConfig(false);
  };

  const handleConfirmExport = async (selectedSections: Set<string>) => {
    setShowExportConfig(false);
    
    await exportToPDF({
      contentRef,
      selectedSections,
      onExportStart: () => setExporting(true),
      onExportEnd: () => setExporting(false),
      onError: (error) => {
        console.error('PDF export failed:', error);
      },
      printDelay: 500
    });
  };

  // ============================================
  // Data Fetching
  // ============================================

  useEffect(() => {
    if (!show) return;
    let disposed = false;

    // Configure moment to use Spanish locale
    moment.locale('es');

    const fetchData = async () => {
      let attributedRequest = false;
      setLoading(true);
      // Do not redisplay a stale opt-in report after a failed session reload.
      setSessionStats(previous => previous?.opener_sales ? null : previous);
      const user = JSON.parse(localStorage.getItem('user') ?? '{}');
      const localId = user.local_id ? Number(user.local_id) : 0;

      try {
        // Fetch session statistics
        let statsResponse;
        if (sessionId) {
          statsResponse = await getSessionStatisticsBySessionId(sessionId);
        } else {
          statsResponse = await getRealTimeSalesStatisticsByLocalId(localId);
        }
        attributedRequest = Boolean(statsResponse.data?.opener_sales);
        if (attributedRequest && disposed) return;
        if (attributedRequest) setPaymentSummary(null);
        setSessionStats(statsResponse.data);

        // Fetch payment transaction summary
        const paymentResponse = await getSessionPaymentTransactionSummaryByLocalId(
          localId,
          sessionId
        );
        if (attributedRequest && disposed) return;
        setPaymentSummary(paymentResponse.data);

        // Fetch modifications for the session
        if (sessionId || statsResponse.data.session_id) {
          const targetSessionId = sessionId || statsResponse.data.session_id;
          try {
            const modificationsResponse = await getAllModificationsBySessionId(targetSessionId);
            if (attributedRequest && disposed) return;
            setModifications(modificationsResponse.data || []);
          } catch (modError) {
            console.error('Error fetching modifications:', modError);
            // Don't fail the whole modal if modifications fail
            setModifications([]);
          }
        }
      } catch (error: any) {
        if (attributedRequest && disposed) return;
        if (attributedRequest) setSessionStats(null);
        console.error('Error fetching session brief data:', error);
        toast.error('Error cargando datos del resumen de sesión');
      } finally {
        if (!attributedRequest || !disposed) setLoading(false);
      }
    };

    fetchData();
    return () => { disposed = true; };
  }, [show, sessionId]);

  // ============================================
  // Render Methods
  // ============================================

  const renderContent = () => {
    if (!sessionStats || !paymentSummary) return null;

    return (
      <div className="session-brief-content" ref={contentRef}>
        <div data-section-id="header">
          <SessionHeader sessionPeriod={sessionPeriod} />
        </div>
        
        <Divider />
        
        <div data-section-id="sales-metrics">
          <SalesMetrics
            netSales={sessionStats.net_sales}
            totalTip={sessionStats.total_tip}
            totalDiscounts={sessionStats.total_discounts}
            validReceiptCount={sessionStats.valid_receipt_count}
            avgTicket={sessionStats.avg_ticket}
            avgTicketPP={sessionStats.avg_ticket_pp}
            totalPeopleServed={sessionStats.total_people_served}
          />
        </div>
        
        <Divider />
        
        <div data-section-id="payment-summary">
          <PaymentSummary typeSummary={paymentSummary.type_summary} />
        </div>
        
        {sessionStats.opener_sales && (
          <div data-section-id="opener-sales">
            <Divider />
            <OpenerSalesSection report={sessionStats.opener_sales} />
          </div>
        )}
        {sessionStats.users_stats && Object.keys(sessionStats.users_stats).length > 0 && (
          <>
            <Divider />
            <div data-section-id="user-stats">
              <UserStatsSection usersStats={sessionStats.users_stats}
                title={sessionStats.opener_sales ? attributionCopy.legacyUsers : undefined} />
            </div>
          </>
        )}
        
        {sessionStats.type_stats && Object.keys(sessionStats.type_stats).length > 0 && (
          <>
            <Divider />
            <div data-section-id="type-stats">
              <TypeStatsSection typeStats={sessionStats.type_stats} />
            </div>
          </>
        )}
        
        {sessionStats.shifts && Object.keys(sessionStats.shifts).length > 0 && (
          <>
            <Divider />
            <div data-section-id="shifts">
              <ShiftsBreakdown shifts={sessionStats.shifts} />
            </div>
          </>
        )}
        
        {modifications.length > 0 && (
          <>
            <Divider />
            <div data-section-id="modifications">
              <ModificationsSummary modifications={modifications} />
            </div>
          </>
        )}
        
        <Divider />
        
        <div data-section-id="footer">
          <SessionFooter />
        </div>
      </div>
    );
  };

  // ============================================
  // Main Render
  // ============================================

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="session-brief-modal"
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            Resumen de Sesión
          </Modal.Title>
        </div>
        <Button variant="none" onClick={onHide} className="modal-close-btn">
          <X size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="sb-loading">
            <Spinner animation="border" variant="primary" />
            <p>Cargando resumen...</p>
          </div>
        ) : (
          renderContent()
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button
            variant="none"
            className="form-button-cancel"
            onClick={onHide}
          >
            Cerrar
          </Button>
          <Button
            variant="none"
            className="form-button-confirm"
            onClick={handleOpenExportConfig}
            disabled={loading || exporting || !sessionStats}
          >
            {exporting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Exportando...
              </>
            ) : (
              <>
                <FileEarmarkPdf className="me-2" />
                Exportar PDF
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>

      {/* Export Configuration Modal */}
      <ExportConfigModal
        show={showExportConfig}
        onHide={handleCloseExportConfig}
        onConfirmExport={handleConfirmExport}
        availableSections={getAvailableSections()}
        isExporting={exporting}
      />
    </Modal>
  );
};

export default SessionBriefModal;
